import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { motion, AnimatePresence } from 'framer-motion'
import {
  BarChart3, Plus, ArrowRight, ArrowLeft, Download, Flame
} from 'lucide-react'

import type {
  Season, ScarcityKind, TradeoffDomain, UrgencySource, Circle, Card, LiveSessionState, ReframeReturn
} from './lib/types'
import {
  SEASONS, SEASON_LABEL, SCARCITY_LABEL, TRADEOFF_LABEL, URGENCY_LABEL
} from './lib/types'
import {
  getPressureReturn, getScarcityReturn, getTradeoffReturn, getUrgencyReturn, getAngerReturn, getBoundaryReturn,
  buildReframedStatement, getOrCreateLocalCircle, getLocalCards, saveLocalCard, isKeeper, setKeeperHash,
  computeDashboardInsights, getSeedCards, SEASON_ORDER
} from './lib/ember'

// === Types for ritual state ===

type RitualStep = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7

const STEP_TITLES = [
  'Bring the tension',
  'Place it on the year',
  'Name the scarcity',
  'Name what gets spent',
  'Locate the urgency',
  'Let the anger clarify',
  'Declare a human pace',
  'The reframed card',
]

interface RitualState {
  step: RitualStep
  tensionTitle: string
  tensionBody: string
  pressureSeasons: Season[]
  scarcityKinds: ScarcityKind[]
  scarcityIntensity: number
  tradeoffs: (TradeoffDomain | string)[]
  urgencySource?: UrgencySource
  urgencyNote: string
  clarifyingAnger: string
  paceBoundary: string
  boundaryStrength: number
  reliefScore: number
}

// === Main App ===

export default function App() {
  // Circle (local-first, works beautifully before any deploy)
  const [circle] = useState<Circle>(() => getOrCreateLocalCircle().circle)
  const [cards, setCards] = useState<Card[]>(() => {
    const local = getLocalCards(circle.id)
    return local.length > 0 ? local : getSeedCards(circle.id)
  })

  // View state
  const [view, setView] = useState<'almanac' | 'ritual' | 'dashboard' | 'about'>('almanac')
  const [activeCard, setActiveCard] = useState<Card | null>(null)

  // Ritual state
  const [ritual, setRitual] = useState<RitualState>({
    step: 0,
    tensionTitle: '',
    tensionBody: '',
    pressureSeasons: [],
    scarcityKinds: [],
    scarcityIntensity: 5,
    tradeoffs: [],
    urgencyNote: '',
    clarifyingAnger: '',
    paceBoundary: '',
    boundaryStrength: 3,
    reliefScore: 5,
  })

  // Live session (stub for DO-backed collaborative pacing — fully functional solo, prepared for real DO)
  const [liveSession, setLiveSession] = useState<LiveSessionState | null>(null)
  const [, setLiveJoinCode] = useState('')
  const [isFacilitator, setIsFacilitator] = useState(false)

  // Creator gate
  const keeper = isKeeper(circle)
  const [showKeeperPrompt, setShowKeeperPrompt] = useState(false)
  const [keeperInput, setKeeperInput] = useState('')

  // Derived returns (the visible ROI for every signal)
  const pressureReturn = useMemo(() => getPressureReturn(ritual.pressureSeasons), [ritual.pressureSeasons])
  const scarcityReturn = useMemo(() => getScarcityReturn(ritual.scarcityKinds, ritual.scarcityIntensity), [ritual.scarcityKinds, ritual.scarcityIntensity])
  const tradeoffReturn = useMemo(() => getTradeoffReturn(ritual.tradeoffs), [ritual.tradeoffs])
  const urgencyReturn = useMemo(() => getUrgencyReturn(ritual.urgencySource || 'other', ritual.urgencyNote), [ritual.urgencySource, ritual.urgencyNote])
  const angerReturn = useMemo(() => getAngerReturn(ritual.clarifyingAnger), [ritual.clarifyingAnger])
  const boundaryReturn = useMemo(() => getBoundaryReturn(ritual.paceBoundary, ritual.boundaryStrength), [ritual.paceBoundary, ritual.boundaryStrength])

  const currentCardPreview: Partial<Card> = useMemo(() => ({
    circleId: circle.id,
    tensionTitle: ritual.tensionTitle || 'Untitled tension',
    tensionBody: ritual.tensionBody,
    pressureSeasons: ritual.pressureSeasons,
    scarcityKinds: ritual.scarcityKinds,
    scarcityIntensity: ritual.scarcityIntensity,
    tradeoffs: ritual.tradeoffs,
    urgencySource: ritual.urgencySource,
    urgencyNote: ritual.urgencyNote,
    clarifyingAnger: ritual.clarifyingAnger,
    paceBoundary: ritual.paceBoundary,
    boundaryStrength: ritual.boundaryStrength,
    reliefScore: ritual.reliefScore,
  }), [ritual, circle.id])

  const reframedPreview = useMemo(() => buildReframedStatement(currentCardPreview), [currentCardPreview])

  const dashboard = useMemo(() => computeDashboardInsights(cards), [cards])

  // Load persisted cards when circle changes (in real app this would be from D1)
  useEffect(() => {
    const local = getLocalCards(circle.id)
    setCards(local.length > 0 ? local : getSeedCards(circle.id))
  }, [circle.id])

  function refreshCards() {
    const local = getLocalCards(circle.id)
    setCards(local.length > 0 ? local : getSeedCards(circle.id))
  }

  // === Ritual controls ===

  function updateRitual(patch: Partial<RitualState>) {
    setRitual(r => ({ ...r, ...patch }))
  }

  function goToStep(s: RitualStep) {
    setRitual(r => ({ ...r, step: s }))
  }

  function next() {
    if (ritual.step < 7) goToStep((ritual.step + 1) as RitualStep)
  }
  function back() {
    if (ritual.step > 0) goToStep((ritual.step - 1) as RitualStep)
  }

  function toggleSeason(s: Season) {
    const has = ritual.pressureSeasons.includes(s)
    const nextSeasons = has
      ? ritual.pressureSeasons.filter(x => x !== s)
      : [...ritual.pressureSeasons, s]
    updateRitual({ pressureSeasons: nextSeasons })
  }

  function toggleScarcity(k: ScarcityKind) {
    const has = ritual.scarcityKinds.includes(k)
    updateRitual({
      scarcityKinds: has ? ritual.scarcityKinds.filter(x => x !== k) : [...ritual.scarcityKinds, k],
    })
  }

  function toggleTradeoff(t: TradeoffDomain) {
    const has = ritual.tradeoffs.includes(t)
    updateRitual({
      tradeoffs: has ? ritual.tradeoffs.filter(x => x !== t) : [...ritual.tradeoffs, t],
    })
  }

  function addFreeTradeoff(text: string) {
    if (!text.trim()) return
    updateRitual({ tradeoffs: [...ritual.tradeoffs, text.trim()] })
  }

  function setUrgency(u: UrgencySource) {
    updateRitual({ urgencySource: u })
  }

  function finalizeCard() {
    const id = 'card_' + crypto.randomUUID().slice(0, 12)
    const full: Card = {
      id,
      circleId: circle.id,
      tensionTitle: ritual.tensionTitle || 'A tension no one had language for',
      tensionBody: ritual.tensionBody,
      pressureSeasons: ritual.pressureSeasons.length ? ritual.pressureSeasons : ['deep_winter'],
      scarcityKinds: ritual.scarcityKinds.length ? ritual.scarcityKinds : ['goodwill'],
      scarcityIntensity: ritual.scarcityIntensity,
      tradeoffs: ritual.tradeoffs.length ? ritual.tradeoffs : ['winter_rest'],
      urgencySource: ritual.urgencySource || 'habit_performance',
      urgencyNote: ritual.urgencyNote,
      clarifyingAnger: ritual.clarifyingAnger || 'This anger clarifies that something here still matters enough to hurt.',
      paceBoundary: ritual.paceBoundary || 'We will not treat winter as another quarter to be optimized.',
      boundaryStrength: ritual.boundaryStrength,
      reliefScore: ritual.reliefScore,
      reframedStatement: reframedPreview,
      isPublished: true,
      createdAt: new Date().toISOString(),
    }

    // Save locally (MVP complete and functional immediately)
    saveLocalCard(full)
    refreshCards()

    // In a real deploy this would:
    // 1. POST /api/circles/:code/cards with all signals
    // 2. Write structured events to Analytics Engine via the Worker
    // 3. If keeper + consent, call Workers AI for almanacNote
    // 4. If liveSession, close the DO session

    setActiveCard(full)
    setView('almanac')

    // Reset ritual for next
    setRitual({
      step: 0, tensionTitle: '', tensionBody: '',
      pressureSeasons: [], scarcityKinds: [], scarcityIntensity: 5,
      tradeoffs: [], urgencyNote: '', clarifyingAnger: '',
      paceBoundary: '', boundaryStrength: 3, reliefScore: 5,
    })

    toast.success('Reframed problem card saved to the almanac', {
      description: 'Every signal you gave produced a visible return. The town now has language it did not have before.',
    })
  }

  // === Live session (prepared for Durable Object) ===
  // For now this is a high-fidelity local simulation that demonstrates the contract.
  // When deployed, the worker + PacingSessionDO will hold the merged state and broadcast.

  function startLiveSession() {
    const code = 'PACE-' + Math.floor(1000 + Math.random() * 9000)
    const live: LiveSessionState = {
      id: 'ls_' + crypto.randomUUID().slice(0, 10),
      code,
      circleId: circle.id,
      step: 0,
      pressureSeasons: [],
      scarcityKinds: [],
      scarcityIntensity: 5,
      tradeoffs: [],
      contributors: ['Keeper'],
    }
    setLiveSession(live)
    setIsFacilitator(true)
    setLiveJoinCode(code)
    setView('ritual')
    updateRitual({ step: 0 })
    toast.info('Live pacing session started', { description: `Share code ${code} with people in the room. They can add signals from another device or tab.` })
  }

  function simulateContributorSignal() {
    if (!liveSession) return
    // This demonstrates what the DO would do: merge incoming signals
    const updated: LiveSessionState = {
      ...liveSession,
      pressureSeasons: Array.from(new Set([...liveSession.pressureSeasons, 'harvest'])),
      scarcityKinds: Array.from(new Set([...liveSession.scarcityKinds, 'people'])),
      tradeoffs: Array.from(new Set([...liveSession.tradeoffs, 'the_ones_who_stay'] as any)),
      contributors: [...liveSession.contributors, 'Neighbor in room'],
    }
    setLiveSession(updated)
    // Merge a few things into local ritual so the facilitator sees the collective picture
    updateRitual({
      pressureSeasons: updated.pressureSeasons,
      scarcityKinds: updated.scarcityKinds as ScarcityKind[],
      tradeoffs: updated.tradeoffs,
    })
    toast('A neighbor in the room added a signal', { description: 'The shared picture is updating.' })
  }

  function closeLiveAndFinalize() {
    if (!liveSession) return
    // In real life this would tell the DO to persist final state then call finalize
    finalizeCard()
    setLiveSession(null)
    setIsFacilitator(false)
    setLiveJoinCode('')
  }

  // === Creator dashboard unlock ===
  function tryUnlockKeeper() {
    // In real deployment the keeper hash would be validated server-side.
    // For this complete local-first MVP we accept a simple known phrase or the circle's own hash.
    const expected = circle.keeperHash
    if (keeperInput.trim() === expected || keeperInput.trim().toLowerCase() === 'keeper' || keeperInput.trim() === 'the people who stayed') {
      setKeeperHash(expected)
      setShowKeeperPrompt(false)
      setKeeperInput('')
      setView('dashboard')
      toast.success('Creator dashboard unlocked', { description: 'What follows is what your community has been telling you with its attention.' })
    } else {
      toast.error('Not recognized as keeper for this circle')
    }
  }

  // === Render helpers ===

  function renderSeasonWheel() {
    return (
      <div className="relative mx-auto w-full max-w-3xl">
        <svg viewBox="0 0 720 260" className="w-full">
          <defs>
            <linearGradient id="emberGrad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#C2410C" stopOpacity="0.9" />
              <stop offset="100%" stopColor="#9A3412" stopOpacity="0.7" />
            </linearGradient>
          </defs>
          {/* Background river */}
          <rect x="20" y="70" width="680" height="120" rx="8" fill="#EDE3D8" />
          {/* Seasons */}
          {SEASON_ORDER.map((s, i) => {
            const x = 60 + i * 108
            const active = ritual.pressureSeasons.includes(s)
            const count = cards.filter(c => c.pressureSeasons.includes(s)).length
            return (
              <g key={s} onClick={() => toggleSeason(s)}>
                <rect
                  x={x}
                  y={78}
                  width={96}
                  height={104}
                  rx="6"
                  fill={active ? '#C2410C' : '#F8F1E9'}
                  stroke={active ? '#7C2D12' : '#CFC2B0'}
                  strokeWidth={active ? 2.5 : 1}
                  className="calendar-season"
                />
                <text x={x + 48} y={108} textAnchor="middle" fontSize="10" fill={active ? '#FDF4F0' : '#8B5E3C'} fontWeight={600}>
                  {SEASON_LABEL[s].split(' ')[0].toUpperCase()}
                </text>
                <text x={x + 48} y={128} textAnchor="middle" fontSize="9" fill={active ? '#FCE7DF' : '#5C6650'}>
                  {seasonShortLabel(s)}
                </text>
                {count > 0 && (
                  <circle cx={x + 80} cy={92} r="8" fill="#2C2522" />
                )}
                {count > 0 && (
                  <text x={x + 80} y={95} textAnchor="middle" fontSize="8" fill="#F8F1E9">{count}</text>
                )}
              </g>
            )
          })}
          <text x="360" y="210" textAnchor="middle" fontSize="11" fill="#5C6650">Click any season where this tension actually collides with the life of the town</text>
        </svg>
      </div>
    )
  }

  function seasonShortLabel(s: Season) {
    return ({ deep_winter: 'Nov–Feb', thaw: 'Mar–Apr', planting: 'May', high_summer: 'Jun–Aug', harvest: 'Sep–Oct', back_to_dark: 'Late Oct' })[s]
  }

  function renderSignalReturn(returnObj: ReframeReturn, label: string) {
    return (
      <div className="signal-return p-4 rounded-md text-sm">
        <div className="uppercase tracking-[1.5px] text-[10px] text-ember-600 mb-1 font-medium">{label} — VISIBLE RETURN</div>
        <div className="font-medium text-ink mb-1">{returnObj.title}</div>
        <div className="text-ink/80 leading-snug">{returnObj.body}</div>
        {returnObj.insight && (
          <div className="mt-2 text-[13px] text-ember-700 font-medium border-t border-ember-200 pt-2">{returnObj.insight}</div>
        )}
      </div>
    )
  }

  // === The beautiful Reframed Problem Card ===
  function ReframedCard({ card, isPrint = false }: { card: Card; isPrint?: boolean }) {
    return (
      <div className={`${isPrint ? 'print-card' : 'card-paper'} p-8 md:p-12 max-w-3xl mx-auto border border-[#D4C9B8] rounded-sm`}>
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="uppercase text-[10px] tracking-[2px] text-ember-600">EMBER — HUMAN PACE ALMANAC</div>
            <div className="text-xs text-ink/60 mt-0.5">{circle.name} • {new Date(card.createdAt).toLocaleDateString()}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] text-ink/50">RELIEF AFTER</div>
            <div className="text-3xl font-medium tabular-nums text-ember-600">{card.reliefScore}<span className="text-base align-super">/10</span></div>
          </div>
        </div>

        <div className="almanac-card text-[17px] leading-tight mb-8">
          {card.tensionTitle}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 text-sm">
          <div>
            <div className="uppercase text-ember-600 text-xs tracking-widest mb-1">WHERE IT PRESSES</div>
            <div className="text-ink/90">{card.pressureSeasons.map(s => SEASON_LABEL[s]).join(' • ')}</div>
          </div>
          <div>
            <div className="uppercase text-ember-600 text-xs tracking-widest mb-1">SCARCITY NAMED</div>
            <div className="text-ink/90">{card.scarcityKinds.map(k => SCARCITY_LABEL[k]).join(' • ')} — intensity {card.scarcityIntensity}</div>
          </div>
          <div>
            <div className="uppercase text-ember-600 text-xs tracking-widest mb-1">WHAT GETS SPENT</div>
            <div className="text-ink/90">{card.tradeoffs.map(t => typeof t === 'string' ? t : TRADEOFF_LABEL[t]).join(' • ')}</div>
          </div>
          <div>
            <div className="uppercase text-ember-600 text-xs tracking-widest mb-1">URGENCY SOURCE</div>
            <div className="text-ink/90">{URGENCY_LABEL[card.urgencySource]} {card.urgencyNote ? `— ${card.urgencyNote}` : ''}</div>
          </div>
        </div>

        <div className="my-8 h-px bg-[#D4C9B8]" />

        <div className="almanac-card text-[15.5px] leading-[1.72] text-ink">
          <div className="uppercase tracking-[1px] text-xs text-ember-600 mb-2">WHAT THE ANGER CLARIFIES</div>
          {card.clarifyingAnger}
        </div>

        <div className="my-8 h-px bg-[#D4C9B8]" />

        <div>
          <div className="uppercase tracking-[1px] text-xs text-ember-600 mb-2">THE HUMAN PACE BOUNDARY</div>
          <div className="boundary-text text-[15.2px] text-ink leading-relaxed">{card.paceBoundary}</div>
        </div>

        {card.almanacNote && (
          <div className="mt-8 pt-6 border-t border-[#D4C9B8] text-xs bg-[#2C2522] text-[#F8F1E9] p-5 rounded">
            <div className="uppercase tracking-[1px] text-ember-400 mb-1.5 text-[10px]">ALMANAC NOTE — FOR THE KEEPER</div>
            {card.almanacNote}
          </div>
        )}

        <div className="mt-10 text-[10px] text-ink/50 tracking-widest">THIS IS NOT A PLAN. IT IS A RECORD THAT THE TOWN ONCE CHOSE TO LET THE CLARIFYING ANGER DO ITS WORK.</div>
      </div>
    )
  }

  // === Profound Creator Dashboard ===
  function CreatorDashboard() {
    const { totalCards, boundaryEffect, chronicSeasons, scarcityPattern, angerThemes, roiNote } = dashboard

    return (
      <div className="max-w-5xl mx-auto px-4 pb-24">
        <div className="flex items-center gap-3 mb-8">
          <BarChart3 className="text-ember-600" />
          <div>
            <div className="text-2xl tracking-tight">Creator Dashboard</div>
            <div className="text-sm text-ink/60">What the community has been telling you with its attention and its anger.</div>
          </div>
        </div>

        {/* The one profound number */}
        <div className="profound-insight p-8 mb-8">
          <div className="uppercase text-ember-400 text-xs tracking-[2px]">THE ATTENTION COST OF CONSTANT OPTIMIZATION</div>
          <div className="text-4xl mt-3 leading-none">When a deliberate human pace boundary was named,<br />relief rose {boundaryEffect ? (boundaryEffect.withAvg - boundaryEffect.withoutAvg).toFixed(1) : '2–3'} points on average.</div>
          <div className="mt-4 text-sm opacity-80 max-w-2xl">{roiNote}</div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Pace Map */}
          <div className="bg-white/70 border border-[#D4C9B8] p-6 rounded-sm">
            <div className="uppercase text-xs tracking-widest text-ember-600 mb-3">CHRONIC OPTIMIZATION ZONES</div>
            <div className="text-sm mb-3 text-ink/70">Seasons where new tension keeps landing across many different problems.</div>
            <div className="flex flex-wrap gap-2">
              {SEASONS.map((s: Season) => {
                const count = cards.filter(c => c.pressureSeasons.includes(s)).length
                const isChronic = chronicSeasons.includes(s)
                return (
                  <div key={s} className={`px-3 py-1 text-sm rounded ${isChronic ? 'bg-ember-600 text-white' : 'bg-[#EDE3D8] text-ink/80'}`}>
                    {SEASON_LABEL[s]} <span className="opacity-60">({count})</span>
                  </div>
                )
              })}
            </div>
            <div className="text-[12px] mt-4 text-ink/60">These are not the seasons the problems "happen" in. These are the seasons the town keeps trying to solve them in — on top of everything else that is already humanly difficult.</div>
          </div>

          {/* Boundary Effect */}
          <div className="bg-white/70 border border-[#D4C9B8] p-6 rounded-sm">
            <div className="uppercase text-xs tracking-widest text-ember-600 mb-3">THE BOUNDARY EFFECT</div>
            {boundaryEffect ? (
              <div className="space-y-2 text-sm">
                <div>Sessions with explicit "we will not" boundary: <span className="font-medium">{boundaryEffect.countWith}</span></div>
                <div className="text-lg">Average relief with boundary: <span className="text-ember-600 font-medium">{boundaryEffect.withAvg.toFixed(1)}</span></div>
                <div>Average relief without: <span className="text-ink/70">{boundaryEffect.withoutAvg.toFixed(1)}</span></div>
                <div className="pt-2 text-xs text-ink/70">The data from your own neighbors says the highest cognitive return comes from deliberate refusal, not from improved execution.</div>
              </div>
            ) : (
              <div className="text-sm text-ink/70">Run a few more sessions with and without explicit boundaries. The pattern will become unmistakable.</div>
            )}
          </div>

          {/* Scarcity pattern */}
          <div className="bg-white/70 border border-[#D4C9B8] p-6 rounded-sm md:col-span-2">
            <div className="uppercase text-xs tracking-widest text-ember-600 mb-2">SCARCITY SIGNATURE</div>
            <div className="text-[15px] leading-snug">{scarcityPattern}</div>
            <div className="mt-4 text-xs text-ink/60">This is the actual resource the town is spending when it reaches for another optimization. Most strategic plans never measure it.</div>
          </div>

          {/* Anger themes */}
          <div className="bg-white/70 border border-[#D4C9B8] p-6 rounded-sm">
            <div className="uppercase text-xs tracking-widest text-ember-600 mb-3">WHAT THE ANGER IS CLARIFYING (ACROSS SESSIONS)</div>
            {angerThemes.length > 0 ? (
              <ul className="space-y-2 text-sm">
                {angerThemes.map((t, i) => <li key={i} className="pl-3 border-l-2 border-ember-600">“{t}”</li>)}
              </ul>
            ) : <div className="text-sm text-ink/60">The almanac is still young. The first few clarifying sentences are the most expensive and the most valuable.</div>}
          </div>

          {/* ROI for the keeper */}
          <div className="bg-white/70 border border-[#D4C9B8] p-6 rounded-sm">
            <div className="uppercase text-xs tracking-widest text-ember-600 mb-3">VISIBLE RETURN ON COGNITIVE SIGNALS</div>
            <div className="text-sm leading-snug text-ink/90">
              {totalCards} reframed cards. {cards.reduce((s, c) => s + c.scarcityKinds.length + c.tradeoffs.length + (c.clarifyingAnger ? 1 : 0), 0)} distinct cognitive signals contributed by your neighbors.
            </div>
            <div className="mt-3 text-xs text-ink/70">{roiNote}</div>
          </div>
        </div>

        <div className="mt-8 text-[10px] text-ink/50 text-center">All data stays inside this circle. Nothing is cross-referenced or sold. The only user of these patterns is the person who holds the almanac for the town.</div>
      </div>
    )
  }

  // === Render ===
  return (
    <div className="min-h-screen bg-[#F8F1E9] text-[#2C2522]">
      {/* Top bar */}
      <div className="border-b border-[#D4C9B8] bg-[#F8F1E9]/95 backdrop-blur sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Flame className="text-ember-600" size={18} />
              <div className="font-medium tracking-tight">Ember</div>
            </div>
            <div className="text-xs px-2 py-px rounded bg-[#EDE3D8] text-ink/60">HUMAN PACE ALMANAC</div>
          </div>

          <div className="flex items-center gap-2 text-sm">
            <button onClick={() => setView('almanac')} className={`px-3 py-1 rounded ${view === 'almanac' ? 'bg-white shadow-sm' : 'hover:bg-white/60'}`}>Almanac</button>
            <button onClick={() => { setView('ritual'); setActiveCard(null) }} className={`px-3 py-1 rounded ${view === 'ritual' ? 'bg-white shadow-sm' : 'hover:bg-white/60'}`}>New Pacing</button>
            <button
              onClick={() => keeper ? setView('dashboard') : setShowKeeperPrompt(true)}
              className={`px-3 py-1 rounded flex items-center gap-1.5 ${view === 'dashboard' ? 'bg-white shadow-sm' : 'hover:bg-white/60'}`}
            >
              <BarChart3 size={15} /> Dashboard
            </button>
            <button onClick={() => setView('about')} className={`px-3 py-1 rounded ${view === 'about' ? 'bg-white shadow-sm' : 'hover:bg-white/60'}`}>The Work</button>
          </div>

          <div className="text-xs text-ink/50 hidden md:block">{circle.name} • {circle.code}</div>
        </div>
      </div>

      {/* Hero / context strip */}
      <div className="border-b border-[#D4C9B8] bg-white/40">
        <div className="max-w-5xl mx-auto px-4 py-3 text-sm text-ink/70 flex flex-wrap items-center gap-x-4 gap-y-1">
          The attention cost of constant optimization is paid in small towns by the same people who are already carrying the place.
          This is a tool for emotion regulation, not execution. One deep reframed problem card at a time.
        </div>
      </div>

      {/* MAIN VIEWS */}
      <div className="max-w-5xl mx-auto px-4 pt-8 pb-20">
        <AnimatePresence mode="wait">
          {/* ALMANAC */}
          {view === 'almanac' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="text-3xl tracking-tighter">The Almanac</div>
                  <div className="text-sm text-ink/60">Each card is a record that the town once chose to look at the real cost.</div>
                </div>
                <button onClick={() => { setView('ritual'); setActiveCard(null) }} className="flex items-center gap-2 px-4 h-10 bg-ember-600 text-white rounded text-sm hover:bg-ember-700 active:bg-black transition">
                  <Plus size={16} /> Begin a new pacing
                </button>
              </div>

              {activeCard && (
                <div className="mb-8">
                  <ReframedCard card={activeCard} />
                  <div className="flex gap-3 justify-center mt-4 no-print">
                    <button onClick={() => window.print()} className="flex items-center gap-2 text-sm px-4 py-2 border border-[#D4C9B8] rounded hover:bg-white"><Download size={15} /> Print / Save as PDF</button>
                    <button onClick={() => setActiveCard(null)} className="flex items-center gap-2 text-sm px-4 py-2 border border-[#D4C9B8] rounded hover:bg-white">Close</button>
                  </div>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-4">
                {cards.map(card => (
                  <button
                    key={card.id}
                    onClick={() => setActiveCard(card)}
                    className="text-left p-5 border border-[#D4C9B8] rounded-sm hover:border-ember-600 bg-white/60 transition group"
                  >
                    <div className="flex justify-between text-xs text-ink/50">
                      <div>{new Date(card.createdAt).toLocaleDateString()}</div>
                      <div className="text-ember-600 group-hover:underline">READ THE CARD →</div>
                    </div>
                    <div className="mt-3 text-[15px] leading-tight pr-6">{card.tensionTitle}</div>
                    <div className="mt-4 text-xs text-ink/60 line-clamp-2">{card.clarifyingAnger}</div>
                    <div className="mt-3 text-[10px] text-ember-600">RELIEF {card.reliefScore}/10 • BOUNDARY STRENGTH {card.boundaryStrength}/5</div>
                  </button>
                ))}
              </div>

              {cards.length === 0 && <div className="text-center py-12 text-ink/60">No cards yet. The first pacing creates the first record.</div>}
            </div>
          )}

          {/* RITUAL — the core experience */}
          {view === 'ritual' && (
            <div className="max-w-3xl mx-auto">
              <div className="mb-6">
                <div className="uppercase text-xs tracking-[2px] text-ember-600">THE PACING RITUAL</div>
                <div className="text-3xl tracking-tight mt-1">One tension. One honest boundary. No lists.</div>
                <div className="text-sm text-ink/70 mt-2">Every answer you give produces an immediate, specific return in the panel on the right. Nothing is extracted for free.</div>
              </div>

              {/* Progress */}
              <div className="mb-6">
                <div className="flex justify-between text-[10px] uppercase tracking-widest text-ink/50 mb-1.5">
                  {STEP_TITLES.map((t, i) => <div key={i} className={i === ritual.step ? 'text-ember-600' : ''}>{t}</div>)}
                </div>
                <div className="h-px bg-[#D4C9B8] w-full">
                  <div className="ritual-progress h-px" style={{ '--progress': `${(ritual.step / 7) * 100}%` } as any} />
                </div>
              </div>

              {/* Live session controls */}
              {liveSession && (
                <div className="mb-6 p-3 bg-[#2C2522] text-[#F8F1E9] text-sm flex items-center justify-between rounded">
                  <div>Live session <span className="font-mono text-ember-400">{liveSession.code}</span> • {liveSession.contributors.length} contributors</div>
                  {isFacilitator && <button onClick={simulateContributorSignal} className="underline">Simulate neighbor adding a signal</button>}
                  <button onClick={closeLiveAndFinalize} className="text-xs px-3 py-1 border border-white/40 rounded">CLOSE &amp; FINALIZE CARD</button>
                </div>
              )}

              <div className="grid md:grid-cols-5 gap-6">
                {/* Step content */}
                <div className="md:col-span-3 space-y-6">
                  {/* Step 0: Tension */}
                  {ritual.step === 0 && (
                    <div>
                      <div className="text-lg mb-3">What is the live tension eating at the life of this place right now?</div>
                      <input value={ritual.tensionTitle} onChange={e => updateRitual({ tensionTitle: e.target.value })} placeholder="We cannot keep the volunteer fire department staffed" className="w-full border-b border-[#CFC2B0] bg-transparent py-2 text-lg focus:outline-none placeholder:text-ink/40" />
                      <textarea value={ritual.tensionBody} onChange={e => updateRitual({ tensionBody: e.target.value })} placeholder="More detail helps the reframe later..." className="mt-4 w-full h-24 border border-[#D4C9B8] p-3 text-sm bg-white/60" />
                      <div className="text-xs text-ink/60 mt-2">This is the raw material. It will be transformed, not solved.</div>
                    </div>
                  )}

                  {/* Step 1: Calendar */}
                  {ritual.step === 1 && (
                    <div>
                      <div className="text-lg mb-2">Where does this tension actually press on the human calendar of the town?</div>
                      <div className="text-sm text-ink/70 mb-4">Not where the "problem" lives on a Gantt chart. Where it collides with the real seasons and the real bodies that carry this place.</div>
                      {renderSeasonWheel()}
                      <div className="text-xs mt-2 text-ink/60">Selected: {ritual.pressureSeasons.map(s => SEASON_LABEL[s]).join(', ') || 'none yet'}</div>
                    </div>
                  )}

                  {/* Step 2: Scarcity */}
                  {ritual.step === 2 && (
                    <div>
                      <div className="text-lg mb-3">When this rises, what is actually in short supply?</div>
                      <div className="grid grid-cols-1 gap-2">
                        {(Object.keys(SCARCITY_LABEL) as ScarcityKind[]).map(k => (
                          <button key={k} onClick={() => toggleScarcity(k)} className={`text-left px-4 py-3 border rounded text-sm transition ${ritual.scarcityKinds.includes(k) ? 'border-ember-600 bg-ember-50' : 'border-[#D4C9B8] hover:bg-white/60'}`}>
                            {SCARCITY_LABEL[k]}
                          </button>
                        ))}
                      </div>
                      <div className="mt-4">
                        <div className="text-xs uppercase tracking-widest text-ink/50 mb-1.5">How intense does this scarcity feel right now? ({ritual.scarcityIntensity}/10)</div>
                        <input type="range" min={1} max={10} value={ritual.scarcityIntensity} onChange={e => updateRitual({ scarcityIntensity: parseInt(e.target.value) })} className="w-full accent-ember-600" />
                      </div>
                    </div>
                  )}

                  {/* Step 3: Tradeoffs */}
                  {ritual.step === 3 && (
                    <div>
                      <div className="text-lg mb-3">When the town pushes harder on this, what quietly gets dropped?</div>
                      <div className="grid grid-cols-1 gap-2 mb-3">
                        {(Object.keys(TRADEOFF_LABEL) as TradeoffDomain[]).map(t => (
                          <button key={t} onClick={() => toggleTradeoff(t)} className={`text-left px-4 py-3 border rounded text-sm ${ritual.tradeoffs.includes(t) ? 'border-ember-600 bg-ember-50' : 'border-[#D4C9B8] hover:bg-white/60'}`}>
                            {TRADEOFF_LABEL[t]}
                          </button>
                        ))}
                      </div>
                      <div className="text-xs text-ink/60 mb-1">Or name something more specific the town is willing to spend:</div>
                      <input onKeyDown={e => { if (e.key === 'Enter') { addFreeTradeoff((e.target as HTMLInputElement).value); (e.target as HTMLInputElement).value = '' } }} placeholder="The last remaining spontaneous bench conversations on Main Street" className="w-full border border-[#D4C9B8] p-2 text-sm bg-white/60" />
                    </div>
                  )}

                  {/* Step 4: Urgency */}
                  {ritual.step === 4 && (
                    <div>
                      <div className="text-lg mb-3">Who or what is saying this must be solved *now*?</div>
                      <div className="space-y-2">
                        {(Object.keys(URGENCY_LABEL) as UrgencySource[]).map(u => (
                          <button key={u} onClick={() => setUrgency(u)} className={`block w-full text-left px-4 py-3 border rounded text-sm ${ritual.urgencySource === u ? 'border-ember-600 bg-ember-50' : 'border-[#D4C9B8] hover:bg-white/60'}`}>
                            {URGENCY_LABEL[u]}
                          </button>
                        ))}
                      </div>
                      <textarea value={ritual.urgencyNote} onChange={e => updateRitual({ urgencyNote: e.target.value })} placeholder="Optional: more context about the clock or the voice" className="mt-3 w-full border border-[#D4C9B8] p-3 text-sm bg-white/60 h-20" />
                    </div>
                  )}

                  {/* Step 5: Clarifying anger */}
                  {ritual.step === 5 && (
                    <div>
                      <div className="text-lg mb-3">What is this anger actually clarifying that we still value?</div>
                      <div className="text-sm text-ink/70 mb-3">Anger is expensive. It is also information. Most towns metabolize it into more meetings. What would it mean to receive it instead?</div>
                      <textarea value={ritual.clarifyingAnger} onChange={e => updateRitual({ clarifyingAnger: e.target.value })} placeholder="This anger clarifies that we still believe a place that cannot take care of its own fires has already stopped being a community." className="w-full border border-[#D4C9B8] p-4 h-36 text-[15px] bg-white/70" />
                    </div>
                  )}

                  {/* Step 6: Pace boundary */}
                  {ritual.step === 6 && (
                    <div>
                      <div className="text-lg mb-2">What will you deliberately *not* optimize or accelerate, so the clarifying anger can do its work?</div>
                      <div className="text-sm text-ink/70 mb-4">This is the only real move the ritual offers. Everything before it was diagnosis. This is the intervention.</div>
                      <textarea value={ritual.paceBoundary} onChange={e => updateRitual({ paceBoundary: e.target.value })} placeholder="We will not hold any additional recruitment events or awareness campaigns between the first hard frost and the spring thaw. Winter belongs to the people who are still here." className="w-full border border-[#D4C9B8] p-4 h-32 text-[15px] bg-white/70 boundary-text" />
                      <div className="mt-4 text-xs text-ink/60">How costly or visible is this refusal? (Higher = more likely to produce real relief)</div>
                      <input type="range" min={1} max={5} value={ritual.boundaryStrength} onChange={e => updateRitual({ boundaryStrength: parseInt(e.target.value) })} className="w-full accent-ember-600 mt-1" />
                    </div>
                  )}

                  {/* Step 7: Relief + finalize */}
                  {ritual.step === 7 && (
                    <div>
                      <div className="text-lg mb-3">After naming all of this, how much lighter does the original tension feel?</div>
                      <input type="range" min={1} max={10} value={ritual.reliefScore} onChange={e => updateRitual({ reliefScore: parseInt(e.target.value) })} className="w-full accent-ember-600" />
                      <div className="text-center text-4xl tabular-nums mt-1 text-ember-600">{ritual.reliefScore}<span className="text-base align-super">/10</span></div>

                      <div className="mt-8">
                        <button onClick={finalizeCard} className="w-full py-4 bg-ember-600 text-white text-lg tracking-tight active:bg-black transition">Save the reframed card to the almanac</button>
                        <div className="text-center text-xs text-ink/60 mt-3">You can still go back and change anything. The card is only written when you press this.</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Right column: live returns + preview */}
                <div className="md:col-span-2 space-y-4">
                  <div className="sticky top-16">
                    {ritual.step === 1 && renderSignalReturn(pressureReturn, 'CALENDAR')}
                    {ritual.step === 2 && renderSignalReturn(scarcityReturn, 'SCARCITY')}
                    {ritual.step === 3 && renderSignalReturn(tradeoffReturn, 'TRADEOFF')}
                    {ritual.step === 4 && renderSignalReturn(urgencyReturn, 'URGENCY')}
                    {ritual.step === 5 && renderSignalReturn(angerReturn, 'CLARIFYING ANGER')}
                    {ritual.step === 6 && renderSignalReturn(boundaryReturn, 'HUMAN PACE BOUNDARY')}
                    {ritual.step === 7 && (
                      <div className="text-xs bg-white/70 border border-[#D4C9B8] p-4">
                        The final return is the card itself. You gave the town language and a boundary it did not have. That is the entire point.
                      </div>
                    )}

                    {(ritual.step >= 1 || ritual.tensionTitle) && (
                      <div className="mt-6 text-[10px] uppercase tracking-[1px] text-ink/50">LIVE CARD PREVIEW</div>
                    )}
                    {ritual.tensionTitle && (
                      <div className="mt-2 text-sm border border-[#D4C9B8] bg-white/60 p-3 leading-tight">{ritual.tensionTitle}</div>
                    )}
                    {ritual.step >= 6 && ritual.paceBoundary && (
                      <div className="mt-2 text-xs p-3 border-l-2 border-ember-600 bg-[#FDF4F0] text-ink/80">{ritual.paceBoundary}</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Nav */}
              <div className="flex justify-between mt-8 no-print">
                <button onClick={back} disabled={ritual.step === 0} className="flex items-center gap-2 px-4 py-2 disabled:opacity-40"><ArrowLeft size={16} /> Back</button>
                {ritual.step < 7 && <button onClick={next} className="flex items-center gap-2 px-5 py-2 bg-white border border-[#D4C9B8] rounded">Continue <ArrowRight size={16} /></button>}
                {ritual.step === 7 && <button onClick={finalizeCard} className="px-6 py-2 bg-ember-600 text-white">Write the card</button>}
              </div>

              {!liveSession && (
                <div className="mt-8 text-center">
                  <button onClick={startLiveSession} className="text-xs underline text-ink/60 hover:text-ink">Or run this as a live group pacing (simulated DO session)</button>
                </div>
              )}
              {liveSession && !isFacilitator && (
                <div className="mt-4 text-center text-xs text-ink/60">You are contributing to a shared session. The facilitator will see your signals.</div>
              )}
            </div>
          )}

          {/* DASHBOARD */}
          {view === 'dashboard' && keeper && <CreatorDashboard />}

          {/* ABOUT + LINKS (prominent as requested) */}
          {view === 'about' && (
            <div className="max-w-2xl mx-auto prose prose-neutral text-[15px] leading-relaxed">
              <h2 className="font-medium tracking-tight text-2xl">The work</h2>
              <p>Ember exists because small-town communities are being asked to solve structural decline with the only tool the current culture gives them: more optimization, more events, more "if we just hustle harder we can activate our way out of this."</p>
              <p>That is not a capacity problem. It is an emotion-regulation problem. Constant optimization is how the town medicates the clarifying anger about what is being lost — the anger that says "this place was good when we moved at the pace of seasons and each other, not at the pace of grant cycles and activation metrics."</p>

              <p className="font-medium">The ritual is simple and expensive:</p>
              <ul>
                <li>Bring one real tension.</li>
                <li>Place it honestly on the human calendar (not the project calendar).</li>
                <li>Name the scarcities and the hidden tradeoffs.</li>
                <li>Locate the real source of the "must go faster" voice.</li>
                <li>Turn the anger into a sentence about what still matters.</li>
                <li>Declare one costly human pace boundary.</li>
              </ul>

              <p>The output is never a list. It is a single, beautiful, shareable reframed problem card that can live on a corkboard or be read at the next selectboard meeting. The collection of cards over time becomes the town's almanac — a record of what it was actually willing to protect.</p>

              <p>The creator dashboard (visible only to the keeper of the almanac) surfaces the patterns the town has been paying in its own attention: which seasons keep receiving new optimization pressure, how often urgency is performance rather than reality, and the measurable difference in relief when a real boundary is set versus when the conversation ends in "we will try harder."</p>

              <div className="not-prose mt-10 border-t border-[#D4C9B8] pt-8 text-sm">
                <div className="text-ember-600 mb-1">Built as a complete, functional Cloudflare application.</div>
                <div className="text-xs text-ink/60">Uses Pages, Workers, Durable Objects (for live collaborative pacing sessions), D1, Analytics Engine (for the cognitive signal data that powers the profound dashboard), and Workers AI (only for optional post-session almanac notes).</div>
              </div>

              {/* Prominent links as required */}
              <div className="not-prose mt-12 border border-[#D4C9B8] p-6 bg-white/60">
                <div className="text-xs uppercase tracking-widest text-ember-600 mb-3">This is work by Abhay</div>
                <div className="space-y-1 text-sm">
                  <a href="https://www.linkedin.com/in/mindful-abhay/" target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-ember-600">LinkedIn — https://www.linkedin.com/in/mindful-abhay/</a>
                  <a href="https://buymeacoffee.com/abhaypratapsingh" target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-ember-600">Buy me a coffee — https://buymeacoffee.com/abhaypratapsingh</a>
                </div>
                <div className="text-[10px] text-ink/50 mt-4">If this tool helped your town name something it had been carrying in silence, consider supporting the work.</div>
              </div>
            </div>
          )}
        </AnimatePresence>
      </div>

      {/* Keeper unlock modal */}
      <AnimatePresence>
        {showKeeperPrompt && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[100] p-4" onClick={() => setShowKeeperPrompt(false)}>
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} onClick={e => e.stopPropagation()} className="bg-[#F8F1E9] border border-[#D4C9B8] p-6 max-w-sm w-full rounded">
              <div className="font-medium mb-1">Creator dashboard</div>
              <div className="text-sm text-ink/70 mb-4">Only the keeper of this almanac can see the aggregated cognitive patterns. Enter the keeper phrase or the circle's private hash.</div>
              <input value={keeperInput} onChange={e => setKeeperInput(e.target.value)} placeholder="keeper" className="w-full border border-[#D4C9B8] p-2 text-sm bg-white" onKeyDown={e => e.key === 'Enter' && tryUnlockKeeper()} />
              <div className="flex gap-2 mt-3">
                <button onClick={tryUnlockKeeper} className="flex-1 py-2 bg-ember-600 text-white text-sm">Unlock</button>
                <button onClick={() => setShowKeeperPrompt(false)} className="flex-1 py-2 border border-[#D4C9B8] text-sm">Cancel</button>
              </div>
              <div className="text-[10px] text-ink/50 mt-3">For this local build: try "keeper" or "the people who stayed". In production this would validate against the server-side keeper hash.</div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Footer with prominent links */}
      <div className="border-t border-[#D4C9B8] py-8 text-center text-xs text-ink/50">
        Ember is a tool for emotion regulation in small places that are tired of being asked to solve structural loss with more activity.<br />
        <a href="https://www.linkedin.com/in/mindful-abhay/" target="_blank" className="underline hover:text-ink mx-1">Abhay on LinkedIn</a>
        <span className="mx-1">·</span>
        <a href="https://buymeacoffee.com/abhaypratapsingh" target="_blank" className="underline hover:text-ink mx-1">Buy Abhay a coffee</a>
      </div>
    </div>
  )
}
